/**
 * Consistent skeleton loader for page transitions.
 * Shows a shimmer animation matching the page layout structure.
 */
export function PageSkeleton() {
  return (
    <div className="animate-pulse space-y-6 p-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <div className="h-7 w-48 bg-gray-700 rounded" />
          <div className="h-4 w-72 bg-gray-800 rounded mt-2" />
        </div>
        <div className="h-9 w-28 bg-gray-700 rounded" />
      </div>

      {/* Stats row skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
            <div className="h-4 w-20 bg-gray-700 rounded mb-2" />
            <div className="h-8 w-16 bg-gray-700 rounded" />
          </div>
        ))}
      </div>

      {/* Content area skeleton */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-6">
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="h-10 w-10 bg-gray-700 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 bg-gray-700 rounded" />
                <div className="h-3 w-1/2 bg-gray-800 rounded" />
              </div>
              <div className="h-6 w-20 bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Empty state placeholder for pages with no data.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ComponentType<{ className?: string }>
  title: string
  description: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && (
        <div className="rounded-full bg-gray-800 p-4 mb-4">
          <Icon className="h-8 w-8 text-gray-400" />
        </div>
      )}
      <h3 className="text-lg font-medium text-white mb-1">{title}</h3>
      <p className="text-sm text-gray-400 max-w-sm mb-4">{description}</p>
      {action}
    </div>
  )
}
